import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { apiFetch, ApiProblem, newIdempotencyKey, type TaskDescriptionDraft } from '../lib/api.ts';
import { useScreen } from '../lib/layout.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { AiCard } from './ui/ai-card.tsx';
import { Button } from './ui/button.tsx';

/** Texte des KI-Entwurfs der Beschreibung (Story 23.21). */
export const TASK_DESCRIPTION_DRAFT_UI_TEXTS = {
  start: 'Entwurf vorschlagen',
  card: 'Entwurf für die Beschreibung',
  waiting: 'Entwurf wird geschrieben …',
  none: 'Kein Entwurf möglich – die KI hatte nichts Passendes. Schreib die Beschreibung gern selbst.',
  meta: 'nichts wird gespeichert, bis du übernimmst',
  accept: 'Übernehmen',
  reject: 'Verwerfen',
  stale: 'Die Beschreibung hat sich seit dem Vorschlag geändert. Der Entwurf wurde nicht übernommen.',
  failed: 'Der Entwurf ist gerade nicht möglich. Versuch es später noch einmal.',
  rejectFailed: 'Verwerfen hat nicht geklappt. Versuch es gleich noch einmal.',
  unsaved: 'Deine Änderung an der Beschreibung ist nicht gespeichert. Der Entwurf wurde nicht übernommen.',
} as const;

const AVAILABLE_KEY = ['task-description-draft-available'] as const;

/** Rolle „Mitarbeiter“ hat keine KI-Entwürfe (Story 23.17): kein ✦, keine Abfrage. */
export function useTaskDescriptionDraftAvailable(): boolean {
  const { getToken, me } = useSession();
  const available = useQuery({
    queryKey: AVAILABLE_KEY,
    queryFn: () => apiFetch<{ available: boolean }>(getToken, '/api/v1/assistant/task-description-drafts/available'),
    staleTime: 30_000,
    enabled: me.role !== 'mitarbeiter',
  });
  return me.role !== 'mitarbeiter' && available.data?.available === true;
}

/** ✦ „Entwurf vorschlagen“ neben der Überschrift „Beschreibung“ (Entwurf Variante A). */
export function DescriptionDraftButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="beschreibung-ki-start"
      className="inline-flex cursor-pointer items-center gap-space-1 rounded-full px-space-2 text-caption text-blue-600 transition-colors duration-(--motion-fast) hover:bg-blue-50 disabled:cursor-default disabled:opacity-50 pointer-coarse:min-h-11 dark:text-link"
    >
      <span aria-hidden>✦</span>
      {TASK_DESCRIPTION_DRAFT_UI_TEXTS.start}
    </button>
  );
}

/**
 * KI-Karte unter der Beschreibung (AD-16): wartet → Vorschlag mit Übernehmen/Verwerfen. „Übernehmen“ läuft über
 * `POST /api/v1/ai/suggestions/{id}/accept` (Command `collab.setTaskFields` als Person); veraltet → 409 `suggestion_stale`
 * mit ruhiger Meldung, nichts überschrieben. Vor dem Übernehmen wartet die Karte auf laufendes Speichern des Blatts
 * (`waitForSaves`), sonst liefen beide gegen dieselbe Version; scheitert das Speichern, übernimmt sie nicht. Während der
 * Übernahme ist das Feld gesperrt (`onAccepting`), damit nichts Getipptes überschrieben wird. Wird die Karte ohne Übernehmen/Verwerfen geschlossen, zieht
 * sie den Entwurf zurück (wartend oder offen, wie „Mehr Felder …“ in der Schnell-Erfassung).
 */
export function DescriptionDraftCard({
  draftId,
  onDone,
  onAccepted,
  waitForSaves,
  onAccepting,
}: {
  draftId: string;
  onDone: () => void;
  onAccepted: () => Promise<void>;
  waitForSaves: () => Promise<boolean>;
  /** Übernahme läuft (`true`) bzw. ist vorbei: das Blatt sperrt so lange das Beschreibungsfeld (Zweit-Review #1584). */
  onAccepting: (accepting: boolean) => void;
}) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const phone = useScreen() === 'phone';
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const finished = useRef(false);
  const draft = useQuery({
    queryKey: ['task-description-draft', draftId],
    queryFn: () => apiFetch<TaskDescriptionDraft>(getToken, `/api/v1/assistant/task-description-drafts/${draftId}`),
    retry: false,
    refetchInterval: (q) => (q.state.data?.state === 'waiting' || (q.state.data === undefined && q.state.status !== 'error') ? 700 : false),
  });

  // Blatt zu oder neuer Entwurf ohne Übernehmen/Verwerfen: Lauf bzw. offenen Vorschlag zurückziehen (spätere Antwort legt
  // nichts an). Der Rückzug wartet einen Takt: StrictMode baut Effekte in der Entwicklung sofort ab und wieder auf.
  const alive = useRef(false);
  useEffect(() => {
    const state = alive;
    state.current = true;
    return () => {
      state.current = false;
      setTimeout(() => {
        if (finished.current || state.current) return;
        void apiFetch(getToken, `/api/v1/assistant/task-description-drafts/${draftId}`, { method: 'DELETE', idempotencyKey: newIdempotencyKey() }).catch(() => undefined);
      }, 0);
    };
  }, [draftId, getToken]);

  const view = draft.data;
  const suggestion = view?.suggestion ?? null;

  const accept = async () => {
    if (!suggestion || busy) return;
    setBusy(true);
    setProblem(null);
    onAccepting(true);
    try {
      // Speichern gescheitert (Warteschlange liefert false): nicht übernehmen, sonst ginge der selbst geschriebene Text
      // verloren (Zweit-Review #1584).
      if (!(await waitForSaves())) {
        setProblem(TASK_DESCRIPTION_DRAFT_UI_TEXTS.unsaved);
        return;
      }
      await apiFetch(getToken, `/api/v1/ai/suggestions/${suggestion.id}/accept`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
      finished.current = true;
      await onAccepted();
      onDone();
    } catch (err) {
      setProblem(err instanceof ApiProblem && err.code === 'suggestion_stale' ? TASK_DESCRIPTION_DRAFT_UI_TEXTS.stale : TASK_DESCRIPTION_DRAFT_UI_TEXTS.failed);
    } finally {
      setBusy(false);
      onAccepting(false);
    }
  };

  // Erst nach erfolgreicher Antwort schließen: sonst bliebe der Vorschlag offen, obwohl er verworfen aussieht (Zweit-Review #1584).
  // Ohne geladenen Vorschlag (Fehlerkarte, „kein Entwurf“) den Lauf selbst zurückziehen, damit keine spätere Antwort noch
  // einen offenen Vorschlag anlegt.
  const reject = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      if (suggestion) {
        await apiFetch(getToken, `/api/v1/ai/suggestions/${suggestion.id}/reject`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
      } else {
        await apiFetch(getToken, `/api/v1/assistant/task-description-drafts/${draftId}`, { method: 'DELETE', idempotencyKey: newIdempotencyKey() });
      }
    } catch (err) {
      // Unbekannter Lauf (404): es gibt nichts zurückzuziehen.
      if (suggestion || !(err instanceof ApiProblem && err.status === 404)) {
        setProblem(TASK_DESCRIPTION_DRAFT_UI_TEXTS.rejectFailed);
        setBusy(false);
        return;
      }
    }
    finished.current = true;
    void queryClient.removeQueries({ queryKey: ['task-description-draft', draftId] });
    onDone();
  };

  if (draft.isError) {
    return (
      <AiCard
        title={TASK_DESCRIPTION_DRAFT_UI_TEXTS.failed}
        pill={null}
        testId="beschreibung-ki-fehler"
        actions={
          <Button variant="secondary" size="sm" className="bg-card pointer-coarse:min-h-11" disabled={busy} onClick={() => void reject()}>
            {TASK_DESCRIPTION_DRAFT_UI_TEXTS.reject}
          </Button>
        }
      >
        {problem ? (
          <p role="alert" data-testid="beschreibung-ki-problem" className="m-0 text-body text-clarify">
            {problem}
          </p>
        ) : null}
      </AiCard>
    );
  }
  if (!view || view.state === 'waiting') return <AiCard title={TASK_DESCRIPTION_DRAFT_UI_TEXTS.waiting} pill={null} testId="beschreibung-ki-wartet" />;
  if (!suggestion) {
    return (
      <AiCard
        title={TASK_DESCRIPTION_DRAFT_UI_TEXTS.none}
        pill={null}
        testId="beschreibung-ki-leer"
        actions={
          <Button variant="secondary" size="sm" className="bg-card pointer-coarse:min-h-11" disabled={busy} onClick={() => void reject()}>
            {TASK_DESCRIPTION_DRAFT_UI_TEXTS.reject}
          </Button>
        }
      >
        {problem ? (
          <p role="alert" data-testid="beschreibung-ki-problem" className="m-0 text-body text-clarify">
            {problem}
          </p>
        ) : null}
      </AiCard>
    );
  }
  return (
    <section aria-label={TASK_DESCRIPTION_DRAFT_UI_TEXTS.card}>
      <AiCard
        testId="beschreibung-ki-karte"
        title={TASK_DESCRIPTION_DRAFT_UI_TEXTS.card}
        confidence={suggestion.confidence}
        meta={`${suggestion.reason.replace(/\.$/, '')} · ${TASK_DESCRIPTION_DRAFT_UI_TEXTS.meta}`}
        actionsBelow
        actions={
          <>
            <Button variant="secondary" size="sm" className="bg-card pointer-coarse:min-h-11" disabled={busy} onClick={() => void reject()} data-testid="beschreibung-ki-verwerfen">
              {TASK_DESCRIPTION_DRAFT_UI_TEXTS.reject}
            </Button>
            <Button variant="primary" size="sm" className="pointer-coarse:min-h-11" disabled={busy || problem === TASK_DESCRIPTION_DRAFT_UI_TEXTS.stale} onClick={() => void accept()} data-testid="beschreibung-ki-uebernehmen">
              {TASK_DESCRIPTION_DRAFT_UI_TEXTS.accept}
            </Button>
          </>
        }
      >
        <p data-testid="beschreibung-ki-text" className={cn('m-0 text-body whitespace-pre-wrap text-ink', phone && 'break-words')}>
          {suggestion.description}
        </p>
        {problem ? (
          <p role="alert" data-testid="beschreibung-ki-problem" className="m-0 text-body text-clarify">
            {problem}
          </p>
        ) : null}
      </AiCard>
    </section>
  );
}

/** Startet den Entwurf; `null` = gerade nicht verfügbar (KI aus oder Budget aufgebraucht) – dann verschwindet der ✦. */
export function useStartDescriptionDraft(): (taskId: string) => Promise<string | null> {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return async (taskId: string) => {
    const started = await apiFetch<{ draftId: string | null }>(getToken, '/api/v1/assistant/task-description-drafts', {
      method: 'POST',
      body: { taskId },
      idempotencyKey: newIdempotencyKey(),
    });
    if (started.draftId === null) void queryClient.invalidateQueries({ queryKey: AVAILABLE_KEY });
    return started.draftId;
  };
}
